import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
}

const UNCATEGORIZED = '__uncategorized__';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim();
  return s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined';
}

// ============================================================================
// Single FAQ item — accordion row with question / answer
// ============================================================================

interface FaqItemProps {
  faq: Faq;
  index: number;
  open: boolean;
  onToggle: () => void;
}

function FaqItem({ faq, index, open, onToggle }: FaqItemProps) {
  const question = hasValue(faq.question) ? faq.question! : `Question ${index + 1}`;
  const answerHtml = hasValue(faq.answer) ? faq.answer! : '';

  const buttonId = `faq-question-${index}`;
  const panelId = `faq-answer-${index}`;

  return (
    <div className={`faq-item${open ? ' faq-item--open' : ''}`}>
      <button
        type="button"
        id={buttonId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
        className="faq-question"
      >
        <span className="faq-question-text">{question}</span>
        <span className="faq-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className="faq-answer"
        dangerouslySetInnerHTML={{ __html: answerHtml }}
      />
    </div>
  );
}

// ============================================================================
// Category block — heading + list of FAQ items
// ============================================================================

interface FaqCategoryProps {
  categoryLabel: string | null;
  faqs: Faq[];
  startIndex: number;
  openIndex: number | null;
  onToggle: (index: number) => void;
}

function FaqCategory({ categoryLabel, faqs, startIndex, openIndex, onToggle }: FaqCategoryProps) {
  const groupRef = useScrollReveal<HTMLDivElement>();

  return (
    <div ref={groupRef} className="reveal-fade-up faq-category">
      {categoryLabel && (
        <h2 className="faq-category-heading">{categoryLabel}</h2>
      )}
      <div className="faq-list">
        {faqs.map((faq, i) => {
          const globalIndex = startIndex + i;
          return (
            <FaqItem
              key={globalIndex}
              faq={faq}
              index={globalIndex}
              open={openIndex === globalIndex}
              onToggle={() => onToggle(globalIndex)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FaqSection() {
  const faqs = useCollectionBinding<Faq>('faqsection.items', {
    collection: 'faqs',
  });

  const emptyMessage = useContentToken('faqsection.empty_message');

  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Group FAQs by category, preserving the collection ordering both for
  // categories and within each category. FAQs without a category collect
  // under a sentinel key rendered without a heading.
  const grouped = useMemo(() => {
    const map = new Map<string, Faq[]>();
    const order: string[] = [];
    for (const faq of faqs) {
      const rawCategory = hasValue(faq.category) ? faq.category!.trim() : '';
      const key = rawCategory || UNCATEGORIZED;
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(faq);
    }
    return { order, map };
  }, [faqs]);

  const hasCategories = grouped.order.some((k) => k !== UNCATEGORIZED);
  const hasFaqs = faqs.length > 0;

  // Precompute the running start-index for each category so item toggling
  // uses a stable global index across the whole page.
  const categoryStartIndices = useMemo(() => {
    const starts: Record<string, number> = {};
    let running = 0;
    for (const key of grouped.order) {
      starts[key] = running;
      running += grouped.map.get(key)!.length;
    }
    return starts;
  }, [grouped]);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up faq-section py-12 md:py-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding
          bindingPath="faqsection.items"
          label="FAQs"
        >
          {hasFaqs ? (
            <div className={`faq-groups${hasCategories ? ' faq-groups--categorized' : ''}`}>
              {grouped.order.map((key) => {
                const items = grouped.map.get(key)!;
                const label = key === UNCATEGORIZED ? null : key;
                return (
                  <FaqCategory
                    key={key}
                    categoryLabel={label}
                    faqs={items}
                    startIndex={categoryStartIndices[key]}
                    openIndex={openIndex}
                    onToggle={handleToggle}
                  />
                );
              })}
            </div>
          ) : (
            <div className="faq-empty">
              <EditableToken
                path="faqsection.empty_message"
                as="p"
                className="font-body text-base text-[var(--vibe-color-text-muted)]"
              >
                {emptyMessage || 'FAQs will be displayed here.'}
              </EditableToken>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }
        .faq-groups--categorized {
          gap: 3rem;
        }

        .faq-category-heading {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-secondary, var(--vibe-color-primary));
          margin: 0 0 1.25rem;
          padding-bottom: 0.6rem;
          border-bottom: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.12));
        }
        @media (min-width: 768px) {
          .faq-category-heading {
            font-size: 1.05rem;
          }
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .faq-item {
          background: #ffffff;
          border: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.12));
          border-radius: 4px;
          overflow: hidden;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }
        .faq-item:hover {
          border-color: var(--vibe-color-primary);
        }
        .faq-item--open {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .faq-question {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.1rem 1.25rem;
          background: transparent;
          border: none;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .faq-question:hover {
          color: var(--vibe-color-primary);
        }
        .faq-question:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }
        @media (min-width: 768px) {
          .faq-question {
            padding: 1.25rem 1.5rem;
            font-size: 1.05rem;
          }
        }

        .faq-question-text {
          flex: 1;
          line-height: 1.4;
        }

        .faq-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-primary);
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }
        .faq-item--open .faq-icon {
          transform: rotate(180deg);
        }

        .faq-answer {
          padding: 0 1.25rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }
        .faq-answer[hidden] {
          display: none;
        }
        @media (min-width: 768px) {
          .faq-answer {
            padding: 0 1.5rem 1.5rem;
            font-size: 1rem;
          }
        }
        .faq-answer :global(p),
        .faq-answer p {
          margin: 0 0 0.75rem;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer ul,
        .faq-answer ol {
          margin: 0 0 0.75rem;
          padding-left: 1.5rem;
        }
        .faq-answer li {
          margin-bottom: 0.35rem;
        }
        .faq-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer strong {
          color: var(--vibe-color-text);
        }

        .faq-empty {
          text-align: center;
          padding: 3rem 1rem;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqSection',
  displayName: 'FAQ Section',
  tokens: {
    'faqsection.empty_message': {
      label: 'Empty State Message',
      type: 'text',
      defaultValue: 'FAQs will be displayed here.',
      description: 'Shown when no FAQs are defined on the property.',
    },
  },
  collectionBindings: {
    'faqsection.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FaqSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqSection;
